
import ProductBase from "@/common/ProductBase.vue"
import jsonldProduct from "@/mixins/jsonld/product"
import breadcrumbsJsonld from "@/mixins/jsonld/breadcrumbs"
import { isValidDate, getCopiedJSON, toNumber } from "@/helpers/helpers"
import { fade } from "@/utils/gsap"
import { stripHtml } from "@/utils/stripHTML"
import { isElectricHeaterProduct } from "@/utils/heaterCalculator"

// Products with a 3D configurator on configure.leisurecraft.com, and where it lives.
// Keyed by product id rather than slug: an EBMS rename changes our slug, while the
// configurator's address is set on their side and has to be updated here by hand.
const CONFIGURATOR_URLS = {
  CU580D: "https://configure.leisurecraft.com/pure-cube-outdoor-original-sauna"
}

export default {
  extends: ProductBase,
  mixins: [jsonldProduct, breadcrumbsJsonld],

  props: {
    product: { type: Object, default: null },
    catalog: { type: Object, default: null }
  },

  data() {
    return {
      self: this,

      hashSyncEnabled: false,

      // Option pairs lifted out of the fragment on arrival, applied once the
      // offer's configuration tree is requested. Not reactive state the template
      // reads — it is a one-shot handover between mounted() and the tree load.
      pendingSelection: [],
      // [{id, count}] lifted out of the same link — the accessories the dealer
      // picked in the configurator, so their rows arrive showing that quantity.
      prefilledAccessories: [],
      // Bumped per configuration request so a superseded response is discarded.
      configurationsRequest: 0,

      isGalleryOpen: false,
      isGallerySticky: false,
      isPriceRequesting: false,
      isPriceRequested: false,
      isConfigurationsLoading: false,
      isScrolledToConfigurations: false,

      compareFiles: [],
      isCompareFilesLoading: true,
      isCompareFilesEmpty: true,
      isCompareDialogOpened: false,

      accessories: [],
      accessoryCategories: [],
      isAccessoriesLoading: true,
      isAccessoriesEmpty: false,

      specsView: "list",
      activeTabsIndex: 0,
      partsTabOpened: false,

      requestAccessories: [],

      heaters: [],
      isHeatersDetailsLoaded: false
    }
  },

  watch: {
    "product.id"() {
      this.trackViewContent()
    },

    offer() {
      this.$set(this.product, "configurations", [])

      if (this.getAllSpecsSelected) {
        this.activeTabsIndex = 0
        this.getOfferMeta(this.offer, "details")
        this.getOfferMeta(this.offer, "documents")
      }

      if ((this.inited || this.getAllSpecsSelected) && this.offer.configurations) {
        this.getConfigurations()
        this.scrollToConfigurations()
      }

      this.syncOfferToHash()
    },

    // Accessories arrive from a different place than the options: the resolver
    // reports an option id it can't place, but a prefilled accessory is matched
    // against this product's accessory list right here, so an id this product no
    // longer offers is simply never looked up by any row. Without this it would
    // disappear in silence — the one case the dealer can't see for themselves.
    isAccessoriesLoading(loading) {
      if (!loading) this.reportUnavailableAccessories()
    }
  },

  computed: {
    getConfiguratorUrl() {
      return CONFIGURATOR_URLS[this.product.id] || null
    },

    isElectricHeater() {
      return isElectricHeaterProduct(this.product)
    },

    // The description, with its "3D Sauna Builder" link (to the old Sauna Customizer
    // on saunabuilder.leisurecraft.com) pointed at the configurator on products that
    // have one. Done here rather than in the copy: that block is pasted into ~24
    // variant descriptions in EBMS, indoor ones too, which have no configurator.
    getDescriptionHtml() {
      const html = this.$options.filters.getCleanEMBSHTML(this.offer.description)

      if (!this.getConfiguratorUrl) return html

      const link = `<a href="${this.getConfiguratorUrl}" target="_blank" rel="noopener">3D Sauna Configurator</a>`

      // The lookahead keeps the match inside the block, so a block without a link
      // can't reach past its </div> and replace some later link in the copy.
      return html.replace(
        /(<div[^>]*class="[^"]*--sauna-builder-link[^"]*"[^>]*>(?:(?!<\/div>)[\s\S])*?)<a\b[\s\S]*?<\/a\s*>/,
        `$1${link}`
      )
    },

    SEO() {
      return {
        // Derived server-side from the product's categories (no per-product flag in
        // EBMS). `!== false` so an older backend that omits the field still indexes,
        // matching CategoryPage -- the two deploy independently.
        index: this.product.searchable !== false,
        follow: true,
        title: this.product.meta_title || this.product.name,
        description: this.product.meta_descr || stripHtml(this.product.description),
        image: this.product.photo,
        type: "product",
        canonical: `/${this.product.slug}`,
        scripts: [this.productJsonldScript, this.breadcrumbsJsonldScript].filter(Boolean)
      }
    },

    // Drives the "N added" badge on a collapsed accessory category. Guests build a
    // price request; a signed-in dealer adds straight to the cart instead.
    getSelectedAccessoryIds() {
      if (this.__isLoggedIn) {
        return (this.CART?.items || []).map((item) => item.id)
      }

      return this.requestAccessories.map((item) => item.id)
    },

    breadcrumbs() {
      if (this.catalog) {
        const breadcrumbs = getCopiedJSON(this.catalog.breadcrumbs)

        breadcrumbs.push({
          name: this.catalog.name,
          treeId: this.catalog.treeId,
          slug: this.catalog.slug
        })

        return breadcrumbs
      }

      return []
    },

    getPrice() {
      if (this.product.configurations?.length) {
        return this.getConfigurationsPrice(this.offer.basePrice)
      }

      return this.getUnitPrice || this.offer.price
    },

    getMSRP() {
      if (this.product.configurations?.length) {
        return this.getConfigurationsPrice(this.offer.msrpBasePrice, "msrpPrice")
      }

      return this.getUnitMSRP || this.offer.msrpPrice
    },

    // US import tariff on what is on screen: Your Price x INVENTRY.TAR_AMT, summed
    // over the selected options the same way the price is. The API sends `tariff`
    // only to dealers billed in the USA (already in dollars, figured on their
    // dealer's Your Price), so for everyone else this is 0 and the notice hides.
    getTariff() {
      if (!this.getAllSpecsSelected) return 0

      const tariff = this.product.configurations?.length
        ? this.getConfigurationsPrice(this.offer.baseTariff, "tariff")
        : this.getUnitTariff || this.offer.tariff

      return toNumber(tariff, 0)
    },

    // "$11.73" as in the owner's mock-up — no space after the sign, unlike __currency.
    getTariffLabel() {
      return this.__currency(this.getTariff).replace(" ", "")
    },

    getCurrentPhotoIndex() {
      return this.offer.photos.indexOf(this.offer.photo)
    },

    getGalleryResponsiveOptions() {
      if (this.offer) {
        return [
          {
            breakpoint: "9999px",
            numVisible: Math.min(16, this.offer.photos.length)
          },
          {
            breakpoint: "1650px",
            numVisible: Math.min(15, this.offer.photos.length)
          },
          {
            breakpoint: "1580px",
            numVisible: Math.min(14, this.offer.photos.length)
          },
          {
            breakpoint: "1500px",
            numVisible: Math.min(13, this.offer.photos.length)
          },
          {
            breakpoint: "1366px",
            numVisible: Math.min(12, this.offer.photos.length)
          },
          {
            breakpoint: "1280px",
            numVisible: Math.min(11, this.offer.photos.length)
          },
          {
            breakpoint: "1150px",
            numVisible: Math.min(10, this.offer.photos.length)
          },
          {
            breakpoint: "1050px",
            numVisible: Math.min(9, this.offer.photos.length)
          },
          {
            breakpoint: "992px",
            numVisible: Math.min(8, this.offer.photos.length)
          },
          {
            breakpoint: "880px",
            numVisible: Math.min(7, this.offer.photos.length)
          },
          {
            breakpoint: "700px",
            numVisible: Math.min(6, this.offer.photos.length)
          },
          {
            breakpoint: "620px",
            numVisible: Math.min(5, this.offer.photos.length)
          },
          {
            breakpoint: "440px",
            numVisible: Math.min(4, this.offer.photos.length)
          },
          {
            breakpoint: "400px",
            numVisible: Math.min(3, this.offer.photos.length)
          },
          {
            breakpoint: "340px",
            numVisible: 2
          }
        ]
      }

      return []
    },

    getCartDisabledMessage() {
      if (!this.getAllSpecsSelected) return "Please select the specifications"

      if (this.getHasUncheckedRequiredConfigurations(this.product.configurations))
        return "Please select all required options"

      return ""
    },

    getProductDetailsTabInfo() {
      return {
        details: this.offer.details,
        files: this.offer.documents.find((d) => d.name === "Product Details")?.childs || []
      }
    },

    getShippingAndAssemblyTabInfo() {
      return {
        raw: this.offer.shipAssm,
        files: this.offer.documents.find((d) => d.name === "Shipping & Assembly")?.childs || []
      }
    },

    getCareAndMaintenanceTabInfo() {
      return {
        raw: this.offer.careMain,
        files: this.offer.documents.find((d) => d.name === "Care & Maintenance")?.childs || []
      }
    },

    // The user's selected options, shaped for the Parts List so the BOM reflects the configured
    // model instead of the super-inventory defaults. One entry per category where a *real* option
    // is chosen (by name, matching EBMS INVENDET/INCOMPDET), with nested picks carried in `childs`
    // (e.g. Interior Options → Benches). We omit categories that are untouched or set to "No option"
    // (id === -1) — in both cases the backend keeps that category's default part (it never removes
    // a default), so there's nothing to send.
    getPartsListConfigurations() {
      const build = (configurations) =>
        configurations
          .filter((configuration) => configuration.type !== "custom") // skip client-only Accessories
          .map((configuration) => {
            const active = configuration.items?.find((item) => item.active)

            if (!active || active.id === -1) return null

            const entry = {
              category: configuration.name,
              id: active.id,
              quan: active.quan
            }

            if (active.nested?.configurations?.length) {
              const childs = build(active.nested.configurations)
              if (childs.length) entry.childs = childs
            }

            return entry
          })
          .filter(Boolean)

      if (!this.product.configurations?.length) return []

      return build(this.product.configurations)
    }
  },

  methods: {
    trackViewContent() {
      const price = Number(this.getPrice)

      this.$fbq?.("ViewContent", {
        content_ids: [String(this.product.id).trim()],
        content_name: this.product.name,
        content_type: "product",
        ...(price > 0 && { value: price, currency: "CAD" })
      })
    },

    async getConfigurations() {
      // The watcher can fire twice on one load — created() settles on a default
      // offer, then mounted() restores the one named in the hash — so responses
      // can land out of order. Only the newest request may touch the page.
      const request = ++this.configurationsRequest

      this.isConfigurationsLoading = true

      // Consumed, not read: an arriving selection applies to the offer in the
      // link, so changing a spec afterwards must not re-apply it.
      const selection = this.pendingSelection
      this.pendingSelection = []

      try {
        const data = selection.length
          ? await this.resolveConfigurations(selection)
          : await this.$axios.$get("configurations", {
              params: {
                id: this.offer.id,
                root: true
              }
            })

        if (request !== this.configurationsRequest) return

        this.$set(this.product, "configurations", data.configurations)

        this.offer.basePrice = data.basePrice
        this.offer.msrpBasePrice = data.msrpBasePrice
        this.offer.baseTariff = data.baseTariff

        if (!selection.length) this.selectFirstConfiguration()

        this.formatConfigurations()
        this.setDefaultConfigurations()

        // Runs after formatConfigurations() because that is what appends
        // Accessories and the "No option" entries the scan has to see.
        // setDefaultConfigurations() leaves an already-active option alone, so a
        // pre-filled tree comes through both untouched.
        if (selection.length) this.focusConfigurationAfterPrefill()

        this.getConfigurationRequiredPhotos()
        this.getHeatersDetails()
      } catch (err) {
        this.__logError(err)
      }

      if (request === this.configurationsRequest) this.isConfigurationsLoading = false
    },

    // One request for a tree the page would otherwise assemble an option at a
    // time (~23 round trips for a Pure Cube, most of them returning nothing).
    // A failure here falls back to the plain tree: arriving with no options
    // selected is recoverable, arriving at a broken page is not.
    async resolveConfigurations(selection) {
      try {
        const data = await this.$axios.$post("configurations-resolve", {
          id: this.offer.id,
          selection
        })

        const dropped = data.report?.unknown || []

        if (dropped.length) {
          // __log is dev-only, and a link whose options no longer resolve is
          // exactly the thing we need to hear about from production.
          this.__logError(`Configuration pre-fill dropped: ${JSON.stringify(dropped)}`)
        }

        // Only an option the group itself rejected is the dealer's problem to fix.
        // A group we never reached ("group-not-found") is usually a step further
        // up that went unanswered — the heater's size decides whether there is a
        // Control Type question at all — and telling them it is "no longer
        // available" sends them looking for something that was never gone. The
        // unfinished group is already opened for them instead.
        const rejected = dropped.filter((entry) => entry.reason === "option-not-in-group")

        if (rejected.length) {
          this.__toastError("Some options in this configuration are no longer available — please re-select them")
        }

        return data
      } catch (err) {
        this.__logError(err)

        return this.$axios.$get("configurations", {
          params: {
            id: this.offer.id,
            root: true
          }
        })
      }
    },

    async getHeatersDetails() {
      if (this.isHeatersDetailsLoaded) {
        this.__log("Heater details are loaded")
        return
      }

      const heaterConfiguration = this.product.configurations.find((c) => c.name.toLowerCase().startsWith("heater"))

      if (!heaterConfiguration) {
        this.__log("No heater configurations")
        return
      }

      try {
        const results = await Promise.allSettled(
          heaterConfiguration.items.map((configurationItem) =>
            this.$axios.$get("configurations", {
              params: {
                id: configurationItem.id,
                root: true
              }
            })
          )
        )

        const ids = new Set(
          results
            .filter((r) => r.status === "fulfilled" && r.value?.configurations)
            .flatMap((r) => r.value.configurations || [])
            .flatMap((conf) => conf.items || [])
            .map((it) => it.id)
            .filter(Boolean)
        )

        if (!ids.size) return

        this.heaters = await this.$axios.$get("heater", {
          params: {
            ids: JSON.stringify(Array.from(ids))
          }
        })

        this.isHeatersDetailsLoaded = true
      } catch (err) {
        this.__logError(err)
      }
    },

    getSpecTooltipContent(spec) {
      if (spec.photo || spec.hoverText) {
        return `
          <div class="product__item__spec__tooltip">
            <div class="${spec.photo ? "product__item__spec__tooltip__photo photo" : "hidden"}">
              <Img src="${spec.photo}" />
            </div>
            <div class="
              ${spec.hoverText ? "product__item__spec__tooltip__text" : "hidden"}
            ">
              ${spec.hoverText}
            </div>
          </div>`
      }

      return null
    },

    setGallerySizeObserver() {
      const checkSize = () => {
        this.isGallerySticky = window.innerHeight > this.$refs.gallery?.offsetHeight + 100
      }

      window.addEventListener("resize", checkSize)

      checkSize()
    },

    setCurrentPhoto(src) {
      this.$set(this.offer, "photo", src)
    },

    setNextOffserCurrentPhoto() {
      if (this.getCurrentPhotoIndex === this.offer.photos.length - 1) {
        this.$set(this.offer, "photo", this.offer.photos[0])
      } else {
        this.$set(this.offer, "photo", this.offer.photos[this.getCurrentPhotoIndex + 1])
      }
    },

    setPrevOffserCurrentPhoto() {
      if (this.getCurrentPhotoIndex === 0) {
        this.$set(this.offer, "photo", this.offer.photos[this.offer.photos.length - 1])
      } else {
        this.$set(this.offer, "photo", this.offer.photos[this.getCurrentPhotoIndex - 1])
      }
    },

    async getAccessories() {
      try {
        const data = await this.$axios.$get("accessories", {
          params: {
            id: this.product.id
          }
        })

        if (data.products?.length) {
          this.accessories = data.products
          this.accessoryCategories = this.groupAccessories(data.products, data.categories)
        } else {
          this.isAccessoriesEmpty = true
        }
      } catch (err) {
        this.__logError(err)
        this.isAccessoriesEmpty = true
      }

      this.isAccessoriesLoading = false
    },

    // The API sends the accessories flat and the EBMS grouping separately, by id.
    // An empty result is normal — a product whose accessory categories are not set
    // up in EBMS yet — and leaves the list ungrouped rather than empty.
    groupAccessories(products, categories) {
      if (!categories?.length) return []

      const byId = new Map(products.map((product) => [product.id, product]))

      return categories
        .map((category) => ({
          name: category.name,
          products: category.productIds.map((id) => byId.get(id)).filter(Boolean)
        }))
        .filter((category) => category.products.length)
    },

    async getOfferMeta(offer, meta) {
      try {
        offer[meta] = await this.$axios.$get(`product-${meta}`, {
          params: {
            id: this.offer.id
          }
        })
      } catch (err) {
        this.__logError(err)
      }
    },

    async getCompareFiles() {
      try {
        this.compareFiles = await this.$axios.$get(`product-documents`, {
          params: {
            id: this.product.id,
            compareModels: true
          }
        })
      } catch (err) {
        this.__logError(err)
      }

      this.isCompareFilesLoading = false
      this.isCompareFilesEmpty = !this.compareFiles?.length

      if (!this.isCompareFilesEmpty) {
        // The button is v-if'd on these flags, so its ref only exists after the
        // re-render this assignment triggers — and not at all if the component was
        // destroyed while the request was in flight, which is why the ref is checked
        // rather than passed straight to GSAP (it warns "target undefined not found").
        this.$nextTick(() => {
          const el = this.$refs["compare-files"]

          if (el) {
            fade(el, {
              duration: 0.5
            })
          }
        })
      }
    },

    scrollToConfigurations() {
      this.$nextTick(() => {
        const divider = this.$refs["configurations-divider"]
        const offset = window?.innerWidth <= 768 ? 80 : 130

        if (divider && !this.isScrolledToConfigurations) {
          this.__scrollIntoView(divider, offset)
          this.isScrolledToConfigurations = true
        }
      })
    },

    getIsValidDate(str) {
      return isValidDate(str)
    },

    // Same treatment an unplaceable option gets, for the same reason: a dealer
    // who was told their build carried three accessories should not have one
    // quietly go missing between the configurator and the cart.
    reportUnavailableAccessories() {
      if (!this.prefilledAccessories.length || this.isAccessoriesLoading) return

      // Trimmed on both sides: these are EBMS ids, which are padded in some
      // tables — the backend strips them in half a dozen places for the same
      // reason, and an id that differs only by a trailing space would otherwise
      // read as "no longer available".
      const key = (value) => String(value ?? "").trim()
      const available = new Set(this.accessories.map((product) => key(product.id)))
      const unavailable = this.prefilledAccessories.filter((entry) => !available.has(key(entry.id)))

      if (!unavailable.length) return

      // Says what it compared against, so a report is enough to tell a genuinely
      // retired accessory from a list that hadn't finished loading.
      this.__logError(
        `Configuration pre-fill dropped accessories: ${JSON.stringify(unavailable)} ` +
          `(product ${this.product.id}, ${this.accessories.length} accessories loaded)`
      )
      this.__toastError("Some accessories from your configuration are no longer available")
    },

    addAccessoryToRequest({ id, count }) {
      // Upsert: the row re-sends itself when its quantity changes, and adding the
      // same accessory twice would put it in the price request twice.
      const existing = this.requestAccessories.find((item) => item.id === id)

      if (existing) {
        existing.count = count
      } else {
        this.requestAccessories.push({ id, count })
      }
    },

    removeAccessoryFromRequest({ id }) {
      this.requestAccessories = this.requestAccessories.filter((item) => item.id !== id)
    },

    onRequestSent() {
      this.isPriceRequested = true
      this.resetConfigurations()

      this.$nextTick(() => {
        this.__scrollIntoView(this.$refs["request-button"], window.innerHeight - 200)
      })
    },

    getFilteredProductFiles(files, extensions) {
      const regex = new RegExp(`\\.(${extensions.join("|")})$`, "i")

      return files.filter((item) => regex.test(item.name))
    },

    // #CU770&Heater%20Option=CUELHE&Front%20Wall%20Left%20%28A1%29=502031…
    // The SKU comes first on its own, then one group=option pair per chosen
    // option — the format the external configurator (configure.leisurecraft.com)
    // builds its "Continue to Order" link with. A bare #CU770 still parses, so
    // links shared before that existed keep working.
    parseHash(raw) {
      const decode = (value) => {
        try {
          return decodeURIComponent(value)
        } catch (e) {
          return value // malformed escape — better a name that fails to match than a crash
        }
      }

      const parts = (raw || "").replace(/^#/, "").split("&")
      const sku = decode(parts.shift() || "")

      const entries = parts
        .map((part) => {
          const at = part.indexOf("=")
          if (at < 1) return null

          return { name: decode(part.slice(0, at)), id: decode(part.slice(at + 1)) }
        })
        .filter((entry) => entry && entry.name && entry.id)

      // Accessories are not an option group — they are separate products with a
      // quantity — so they travel as one `Accessories=id:count,id:count` entry
      // and are split out here. Left in the selection they would be sent to the
      // resolver as a group that does not exist and quietly dropped.
      const accessories = entries
        .filter((entry) => entry.name.toLowerCase() === "accessories")
        .flatMap((entry) => entry.id.split(","))
        .map((part) => {
          const [id, count] = part.split(":")

          return { id: (id || "").trim(), count: Math.max(1, parseInt(count, 10) || 1) }
        })
        .filter((entry) => entry.id)

      const selection = entries.filter((entry) => entry.name.toLowerCase() !== "accessories")

      return { sku, selection, accessories }
    },

    // Restore the shared configuration from the URL fragment.
    // The fragment is client-only (never sent to the server / ignored by crawlers),
    // so this runs after hydration and reuses the existing spec→offer resolution.
    restoreOfferFromHash() {
      this.restoreOffer(this.parseHash(window.location.hash))
    },

    // The Saunas menu links models straight to their offer (/ct-cabin-saunas#CTC68).
    // Arriving from another page, mounted() restores that from the fragment; on
    // this product already the page is reused and nothing re-reads the fragment,
    // so the menu hands the SKU over and it's treated as a bare #SKU link.
    selectOfferFromMenu(sku) {
      this.restoreOffer({ sku, selection: [], accessories: [] })
    },

    restoreOffer({ sku, selection, accessories }) {
      if (!sku || !this.product.offers?.length) return

      const offer = this.product.offers.find((o) => o.id === sku)
      if (!offer) return

      // Unlike the options, these need no resolving — they are product ids with a
      // quantity, and the accessory rows read them directly.
      this.prefilledAccessories = accessories

      // The list is usually still loading at this point and the watcher covers
      // it; this catches the case where it had already arrived.
      this.reportUnavailableAccessories()

      // Held rather than read later: resolving the specs below fires the offer
      // watcher, and syncOfferToHash rewrites the fragment down to the bare SKU.
      // The option pairs are gone from the URL by the time the tree arrives.
      this.pendingSelection = selection

      offer.specs.forEach((offerSpec) => {
        const spec = this.product.specs.find((s) => s.name === offerSpec.name)
        if (spec) spec.active = offerSpec.value
      })

      this.checkIfSpecsAvailable()
      this.checkAllSpecs()
    },

    // Where a dealer arriving from the configurator should land.
    //
    // Something still unanswered -> that group, so an incomplete link doesn't
    // leave them at a disabled Add to Cart with twenty groups to search.
    //
    // Nothing unanswered -> the last step, which is Accessories. They answered
    // every configuration question in the configurator already; opening them on
    // group one would mean clicking "Next step" twenty times to reach the only
    // thing left to do. Optional groups left unset don't count as unanswered —
    // setDefaultConfigurations has already resolved those to "No option".
    focusConfigurationAfterPrefill() {
      const unfinished = this.product.configurations.find(
        (configuration) =>
          configuration.type !== "custom" && this.getHasUncheckedRequiredConfigurations([configuration])
      )

      const last = this.product.configurations[this.product.configurations.length - 1]

      this.setActiveConfiguration(unfinished || last)
    },

    // Mirror the selected variant into the URL fragment so it can be shared.
    // replaceState keeps it a single page (no navigation, no history spam, no SEO impact).
    syncOfferToHash() {
      if (!this.hashSyncEnabled || typeof window === "undefined") return

      const base = window.location.pathname + window.location.search
      // A variant is "shareable" once all specs are picked. We intentionally do NOT
      // compare offer.id to product.id: a super product can itself be one of the
      // variants (e.g. HU6112N), and that offer must still get its own hash.
      const sku = this.getAllSpecsSelected && this.product.specs?.length && this.offer?.id ? this.offer.id : null

      window.history.replaceState(window.history.state, "", sku ? `${base}#${encodeURIComponent(sku)}` : base)
    }
  },

  mounted() {
    this.trackViewContent()
    this.setGallerySizeObserver()

    this.restoreOfferFromHash()
    this.$nextTick(() => {
      this.hashSyncEnabled = true
    })

    this.$root.$on("product:select-offer", this.selectOfferFromMenu)
  },

  beforeDestroy() {
    this.$root.$off("product:select-offer", this.selectOfferFromMenu)
  },

  created() {
    this.getAccessories()
    this.getCompareFiles()
  }
}
